<template>
  <div class="flex flex-col gap-y-[24px]">
    <div class="flex flex-col gap-y-[8px]" v-for="(field, index) in formConfig.fields" :key="`${field.name}-${index}`">
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        <RequiredIndicator :filled="!!formData[field.name] && !isNumberFieldInvalid(field.name)" />
          {{ field.label }}
        </div>
      <div class="relative">
        <VInput
          v-if="field.type === 'number'"
          :value="formData[field.name] ?? ''"
          :placeholder="field.placeholder || 'Введите значение'"
          contentWidth
          type="number"
          min="0"
          step="any"
          :disabled="disabled"
          :error="isNumberFieldInvalid(field.name)"
          @input="onNumberInput(field.name, $event)"
          @blur="validateNumberField(field.name)"
        />
        <div
          v-if="isNumberFieldInvalid(field.name)"
          class="note-12-reg-400 text-v-ui-root-service-alarm mt-[4px]"
        >
          Заполните обязательное поле
        </div>

        <VSelector
          v-if="field.type === 'select'"
          v-model="formData[field.name]"
          :items="preparedOptions(field.options, field.multiple)"
          absolute
          :disabled="disabled"
          titleKey="title"
          size="m"
          :multiselect="field.multiple"
          valueKey="id"
          variant="gray"
          :placeholder="field.placeholder || 'Выберите значение'"
      />
      </div>
    </div>
  </div>
</template>

<script>
import { VInput, VSelector } from '@gostelemed.front/rt-doctis.designsystem';
import RequiredIndicator from '@/components/base/RequiredIndicator.vue';
export default {
  name: 'DynamicAlghoritmForm',
  components: {
    VInput,
    VSelector,
    RequiredIndicator,
  },
  props: {
    formConfig: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fullKzsFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {},
      isInitializing: true,
      touchedNumberFields: {},
    }
  },

  mounted() {
    this.generateFormStructure();
    this.parseInitialData();
    this.$nextTick(() => {
      this.isInitializing = false;
      this.emitStepResult();
    });
  },

  watch: {
    formData: {
      deep: true,
      handler() {
        if (this.isInitializing) return;
        this.emitStepResult();
      },
    },
  },

  methods: {
    /**
     * Оставляет в поле цифры и одну десятичную точку.
     * @param {string} fieldName
     * @param {string|number} value
     */
    onNumberInput(fieldName, value) {
      const numberValue = String(value ?? '')
        .replace(/[^\d.]/g, '')
        .replace(/(\..*)\./g, '$1');

      this.$set(this.formData, fieldName, numberValue || null);
    },
    /**
     * Заполняет форму ранее сохраненными значениями.
     */
    parseInitialData() {
      this.formConfig.fields.forEach((field) => {
        const currentValue = this.getParamsValueByPath(field.name);
        if (currentValue === null || typeof currentValue === 'undefined') return;

        const preparedValue = field.type === 'select'
          ? this.findSelectOption(field.options, currentValue, field.multiple)
          : currentValue;

        this.$set(this.formData, field.name, preparedValue);
      });
    },
    /**
     * Помечает number-поле как затронутое.
     * @param {string} fieldName
     */
    validateNumberField(fieldName) {
      this.$set(this.touchedNumberFields, fieldName, true);
    },
    /**
     * Проверяет невалидность number-поля.
     * @param {string} fieldName
     * @returns {boolean}
     */
    isNumberFieldInvalid(fieldName) {
      if (!this.touchedNumberFields[fieldName]) return false;
      const value = this.formData[fieldName];
      return value === null
        || typeof value === 'undefined'
        || value === ''
        || Number.isNaN(Number(value));
    },
    /**
     * Создает начальную структуру значений формы.
     */
    generateFormStructure() {
      this.formData = {};

      this.formConfig.fields.forEach((field) => {
        let defaultValue = null;
        if (field.type === 'select') {
          defaultValue = field.multiple ? [] : null;
        }
        this.$set(this.formData, field.name, defaultValue);
      });
    },

    /**
     * Записывает значение в объект по пути через точку.
     * @param {Object} target
     * @param {string} path
     * @param {*} value
     */
    setNestedValue(target, path, value) {
      const parts = path.split('.');
      let cursor = target;

      parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;

        if (isLast) {
          this.$set(cursor, part, value);
          return;
        }

        if (!cursor[part] || typeof cursor[part] !== 'object') {
          this.$set(cursor, part, {});
        }

        cursor = cursor[part];
      });
    },

    /**
     * Читает значение параметра алгоритма по пути.
     * @param {string} path
     * @returns {*}
     */
    getParamsValueByPath(path) {
      const params = this.fullKzsFormData?.algorithm?.params;
      if (!params || !path) return undefined;

      return path.split('.').reduce((acc, key) => {
        if (acc && typeof acc === 'object') {
          return acc[key];
        }
        return undefined;
      }, params);
    },
    /**
     * Нормализует значение select к массиву примитивов.
     * На выходе всегда массив, даже для одиночного выбора.
     * @param {*} value
     * @returns {Array}
     */
    normalizeSelectOptionValues(value) {
      const source = Array.isArray(value) ? value : [value];
      return source
        .map((item) => item?.value ?? item?.id ?? item)
        .filter((item) => item !== null && typeof item !== 'undefined' && item !== '');
    },
    /**
     * Находит option для select по сохраненному значению.
     * @param {Array} options
     * @param {*} value
     * @returns {Object|null}
     */
    findSelectOption(options = [], value, multiple = false) {
      const prepared = this.preparedOptions(options, multiple);
      const normalizedValues = this.normalizeSelectOptionValues(value);
      const selectedOptions = prepared.filter(
        (item) => normalizedValues.some((selected) => selected == item.id)
      );

      return multiple ? selectedOptions : (selectedOptions[0] || null);
    },
    /**
     * Преобразует select-значение в формат params (массив).
     * @param {*} value
     * @returns {Array}
     */
    toSelectParamValue(value, multiple = false) {
      return this.normalizeSelectOptionValues(value);
    },

    /**
     * Формирует итоговый объект параметров шага.
     * @returns {Object}
     */
    generateStepResutl() {
      // Вносим параметры по умолчанию
      const result = {
        ...this.formConfig?.defaultParams
      };

      // Вносим параметры из формы
      this.formConfig.fields.forEach((field) => {
        const fieldValue = this.formData[field.name];
        let value = fieldValue;

        if (field.type === 'select') {
          value = this.toSelectParamValue(fieldValue, field.multiple);
        } else if (field.type === 'number' && fieldValue !== null && fieldValue !== '') {
          value = Number(fieldValue);
        }

        if (field.name.includes('.')) {
          this.setNestedValue(result, field.name, value);
        } else {
          result[field.name] = value;
        }
      });

      return result;
    },

    /**
     * Эмитит актуальный результат формы наверх.
     */
    emitStepResult() {
      this.$emit('on-update-step-result', this.generateStepResutl());
    },

    /**
     * Подготавливает опции под формат VSelector.
     * @param {Array} options
     * @returns {Array}
     */
    preparedOptions(options = [], multiple = false) {
      return options.map(item => ({
        id: item.value,
        title: item.label,
        multiselect: multiple,
        ...item,
      }));
    },
  }
}
</script>
